import {
  usePutAdminsUserId,
  usePutAdminsUserIdProfileimage,
  getGetAdminsUserIdQueryKey,
} from '@/api/generated/admin/admin'
import {
  usePutProvidersUserId,
  usePutProvidersUserIdProfileimage,
  getGetProvidersUserIdQueryKey,
} from '@/api/generated/doctor/doctor'
import type { AdminApiInContract, UpdateDoctorAdminApiInContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { useQueryClient } from '@tanstack/react-query'
import { useAppDispatch } from '@/redux/hooks/useAppDispatch'
import { EDIT_ADMIN_MEMBER_ERROR, EDIT_PROVIDER_MEMBER_ERROR } from '@/store/newMember/newMemberConstants'
import { getMemberApiErrorPayload } from './getMemberApiErrorPayload'

/**
 * Edits a clinic member (admin or provider) and, when a new profile image was selected,
 * uploads it against the same UserId.
 *
 * Replaces the `editAdminMember`/`editProviderMember` thunks + EDIT_*_MEMBER_SUCCESS reducer
 * flow. The old success path wrote `editedXMemberData` into `state.newClinicMember`, which a
 * member.jsx effect merged into Redux `members[id]` (`setEditedMemberData`) and then ran the
 * image-upload + `pageState: 'success'` chain. Here the optimistic merge is unnecessary: on
 * success the member query is invalidated so the displayed member refetches the saved values,
 * and `onSuccess` drives the same edit-mode exit the old `pageState === 'success'` did.
 *
 * On failure the existing EDIT_*_MEMBER_ERROR reducer cases are reused verbatim by dispatching
 * them with the API error body, so the field-level error mapping (EmailNotUnique,
 * EmailCannotBeModified, PhoneNumberNotUnique, etc.) surfaces through `errorNewMember` exactly
 * as before.
 */
interface EditAdminArgs {
  role: 'Admin'
  body: AdminApiInContract
  image: Blob | null
}

interface EditProviderArgs {
  role: 'Provider'
  body: UpdateDoctorAdminApiInContract
  image: Blob | null
}

export type EditMemberArgs = EditAdminArgs | EditProviderArgs

export function useEditMember(userId: string) {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  const editAdmin = usePutAdminsUserId()
  const editProvider = usePutProvidersUserId()
  const uploadAdminImage = usePutAdminsUserIdProfileimage()
  const uploadProviderImage = usePutProvidersUserIdProfileimage()

  async function editMember(
    args: EditMemberArgs,
    opts?: { onSuccess?: () => void; onError?: (error: unknown) => void },
  ): Promise<void> {
    const isAdmin = args.role === 'Admin'
    const invalidateMember = () =>
      void queryClient.invalidateQueries({
        queryKey: isAdmin ? getGetAdminsUserIdQueryKey(userId) : getGetProvidersUserIdQueryKey(userId),
      })
    const handleError = (error: unknown) => {
      dispatch({
        type: isAdmin ? EDIT_ADMIN_MEMBER_ERROR : EDIT_PROVIDER_MEMBER_ERROR,
        payload: getMemberApiErrorPayload(error),
        failed: true,
      })
      opts?.onError?.(error)
    }

    try {
      if (args.role === 'Admin') {
        await editAdmin.mutateAsync({ userId, data: args.body })
      } else {
        await editProvider.mutateAsync({ userId, data: args.body })
      }
    } catch (error) {
      // The member update itself failed — nothing persisted, so there's no stale cache to refresh.
      handleError(error)
      return
    }

    // The member update persisted. From here the member query MUST be invalidated even if the
    // optional image upload fails — otherwise the page would keep showing stale data for a save
    // that actually landed.
    try {
      if (args.image) {
        await (isAdmin ?
          uploadAdminImage.mutateAsync({ userId, data: { image: args.image } })
        : uploadProviderImage.mutateAsync({ userId, data: { image: args.image } }))
      }
      invalidateMember()
      opts?.onSuccess?.()
    } catch (error) {
      invalidateMember()
      handleError(error)
    }
  }

  const isPending =
    editAdmin.isPending || editProvider.isPending || uploadAdminImage.isPending || uploadProviderImage.isPending

  return { editMember, isPending }
}
